import { type SessionResult } from '../components/sessionSummaryData';
import { type FamilyMasteryState, getGrowthStage } from '../../mastery';
import { type AppState } from '../../app/store/types';
import { type GroupId } from '../../accounts/types';
import { useSessionTip } from './useSessionTip';
import { hasRepeatedStruggle } from '../components/sessionSummary/recentSessionScores';
import { isStreakMilestone } from '../../utils/streakMilestones';
export type ExperienceMode = 'quick' | 'celebrated' | 'reflection';
export interface CelebrationTriggers {
  foutloos: boolean;
  recordBroken: boolean;
  stageUpgrades: {
    familyId: string;
    oldStage: number;
    newStage: number;
  }[];
  streakMilestone: number | null;
}
export interface SessionExperience {
  mode: ExperienceMode;
  triggers: CelebrationTriggers;
  djayTip: string;
  djayEmotion: string;
  /** True when 2+ of last 3 sessions were <50% — used for softer Reflection copy. */
  repeatedStruggle: boolean;
}
export function useSessionExperience(sessionAnswers: SessionResult[], preFamilyMastery: Record<string, FamilyMasteryState>, currentFamilyMastery: Record<string, FamilyMasteryState>, labMode: string | undefined, state: AppState, selectedGroup: GroupId, recordsBroken: boolean): SessionExperience {
  // ── Compute triggers (synchronous, no hooks) ────────────
  const total = sessionAnswers.length;
  const correct = sessionAnswers.filter(a => a.isCorrect).length;
  const accuracy = total > 0 ? correct / total : 0;
  const foutloos = total > 0 && correct === total;
  const stageUpgrades: {
    familyId: string;
    oldStage: number;
    newStage: number;
  }[] = [];
  const practicedFamilyIds = Array.from(new Set(sessionAnswers.map(a => a.familyId).filter(Boolean))) as string[];
  practicedFamilyIds.forEach(familyId => {
    const preKey = Object.keys(preFamilyMastery).find(k => k.startsWith(familyId));
    const postKey = Object.keys(currentFamilyMastery).find(k => k.startsWith(familyId));
    const preScore = preKey ? preFamilyMastery[preKey].masteryScore : 0;
    const postScore = postKey ? currentFamilyMastery[postKey].masteryScore : 0;
    const oldStage = getGrowthStage(preScore).level;
    const newStage = getGrowthStage(postScore).level;
    if (newStage > oldStage) {
      stageUpgrades.push({
        familyId,
        oldStage,
        newStage
      });
    }
  });
  const streak = state.streak?.currentTotalStreak || 0;
  // Eén lijst met de burst in de sessie. Deze regel had een eigen kopie met 50
  // in plaats van 60, dus vierde de samenvatting bij een stand waarop de sessie
  // zweeg — en zweeg bij een stand waarop de sessie juichte.
  const streakMilestone = isStreakMilestone(streak) ? streak : null;
  const triggers: CelebrationTriggers = {
    foutloos,
    recordBroken: recordsBroken,
    stageUpgrades,
    streakMilestone
  };

  // ── Determine mode ──────────────────────────────────────
  let mode: ExperienceMode = 'quick';
  if (labMode) {
    mode = 'quick'; // Lab modes always quick
  } else if (foutloos || recordsBroken || stageUpgrades.length > 0 || streakMilestone) {
    mode = 'celebrated';
  } else if (accuracy < 0.3 && total >= 3) {
    mode = 'reflection';
  }

  // Decay-detection: soften copy when the kid has struggled repeatedly (§20.7 G6).
  const repeatedStruggle = !labMode && total >= 3 && hasRepeatedStruggle();

  // ── Resolve tip with concrete session data (§20.4) ──────
  const tip = useSessionTip({
    sessionAnswers,
    recordBroken: recordsBroken,
    stageUpgrades,
    foutloos,
    streakMilestone,
    labMode,
    isReflection: mode === 'reflection',
    state,
    selectedGroup,
    repeatedStruggle
  });
  return {
    mode,
    triggers,
    djayTip: tip.text,
    djayEmotion: tip.emotion,
    repeatedStruggle
  };
}